import React, { useState } from "react";
import {
  Car,
  ChevronRight,
  Mail,
  MapPin,
  MessageCircle,
  Pencil,
  Phone,
  Plus,
  Save,
  Unlink,
  Users,
} from "lucide-react";
import { totalOS } from "./os";
import { abrirWhatsApp, Btn, Busca, Field, Modal, Pill, Placa, Vazio, usarServidor } from "../ui/componentes";
import { ST, brl, fdata, num } from "../ui/formato";

/* ══════════════════════════════════════════════════════════════════
   CLIENTES (CRM)
   ══════════════════════════════════════════════════════════════════ */
function Clientes({ db, set, toast, abrirOS, novaOS, acoes }) {
  const { executar, ocupado } = usarServidor(toast);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [novo, setNovo] = useState(false);
  const [novoVei, setNovoVei] = useState(null);
  /* Editar reaproveita o mesmo formulário do cadastro: dois formulários para
     os mesmos campos divergem na primeira vez que alguém acrescenta um. */
  const [editando, setEditando] = useState(null);
  const [desvinculando, setDesvinculando] = useState(null);
  const [form, setForm] = useState({ nome: "", tipo: "PF", doc: "", tel: "", email: "", cidade: "", end: "", obs: "" });
  const [fv, setFv] = useState({ placa: "", marca: "", modelo: "", ano: "", cor: "", km: "", comb: "Flex", chassi: "" });

  const totalOS = (o) => o.itens.reduce((s, i) => s + i.qtd * i.valor, 0) - (o.desconto || 0);
  const veiculosDe = (id) => db.veiculos.filter((v) => v.cli === id);
  const osDe = (id) => db.os.filter((o) => o.cli === id);
  const gastoDe = (id) => osDe(id).filter((o) => ["finalizada", "entregue"].includes(o.status)).reduce((s, o) => s + totalOS(o), 0);
  const ultima = (id) => osDe(id).map((o) => o.abertura).sort().reverse()[0];

  const lista = db.clientes.filter((c) => {
    const t = q.toLowerCase();
    return !t || c.nome.toLowerCase().includes(t) || c.doc.includes(t) || c.tel.includes(t) ||
      veiculosDe(c.id).some((v) => v.placa.toLowerCase().includes(t.replace(/[^a-z0-9]/gi, "")) || v.modelo.toLowerCase().includes(t));
  });

  const salvarCliente = () => executar(
    () => {
      if (!form.nome.trim()) throw new Error("Todo cliente precisa de um nome.");
      // A versão vai junto no form (veio do cliente aberto): se alguém no
      // balcão mexeu no mesmo cadastro, o servidor recusa em vez de
      // sobrescrever calado.
      return editando ? acoes.atualizarCliente(editando, form) : acoes.criarCliente(form);
    },
    (c) => {
      const era = editando;
      setNovo(false); setEditando(null);
      setForm({ nome: "", tipo: "PF", doc: "", tel: "", email: "", cidade: "", end: "", obs: "" });
      toast(era ? "Cliente atualizado" : "Cliente cadastrado",
        era ? `Os dados de ${c.nome} valem para o histórico inteiro.`
            : `${c.nome} já pode receber orçamentos.`);
    },
    "Não deu para salvar");

  const salvarVeiculo = () => executar(
    () => {
      if (!fv.placa.trim() || !fv.modelo.trim()) {
        throw new Error("Sem placa e modelo o veículo não aparece nas buscas.");
      }
      return acoes.criarVeiculo({ ...fv, cli: novoVei });
    },
    (v) => {
      setNovoVei(null);
      setFv({ placa: "", marca: "", modelo: "", ano: "", cor: "", km: "", comb: "Flex", chassi: "" });
      toast("Veículo vinculado", `${v.marca} ${v.modelo} — placa ${v.placa}.`);
    },
    "Não deu para vincular");

  const c = sel ? db.clientes.find((x) => x.id === sel) : null;

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Busca v={q} set={setQ} ph="Buscar por nome, CPF/CNPJ, telefone ou placa…" />
        <Btn variant="p" icon={Plus} onClick={() => setNovo(true)}>Novo cliente</Btn>
      </div>

      <div className="card" style={{ overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table className="tbl">
            <thead><tr><th>Cliente</th><th>Contato</th><th>Veículos</th><th>Última visita</th><th className="num">Gasto acumulado</th><th></th></tr></thead>
            <tbody>
              {lista.map((cl) => (
                <tr key={cl.id} style={{ cursor: "pointer" }} onClick={() => setSel(cl.id)}>
                  <td>
                    <div className="flex items-center gap-2.5">
                      <span className="mono flex items-center justify-center flex-none" style={{ width: 30, height: 30, borderRadius: 5, background: cl.tipo === "PJ" ? "var(--gulf-d)" : "var(--ink)", color: "#fff", fontSize: 11, fontWeight: 600 }}>
                        {cl.nome.split(" ").slice(0, 2).map((w) => w[0]).join("")}
                      </span>
                      <span>
                        <span className="block text-sm font-semibold leading-tight">{cl.nome}</span>
                        <span className="block text-xs t-mut mono">{cl.doc}</span>
                      </span>
                    </div>
                  </td>
                  <td className="text-sm"><span className="block mono">{cl.tel}</span><span className="block text-xs t-mut">{cl.cidade}</span></td>
                  <td><div className="flex flex-wrap gap-1.5">{veiculosDe(cl.id).map((v) => <Placa key={v.id} n={v.placa} />)}</div></td>
                  <td className="text-sm t-mut">{fdata(ultima(cl.id))}</td>
                  <td className="num text-sm font-semibold">{brl(gastoDe(cl.id))}</td>
                  <td><ChevronRight size={15} className="t-mut2" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {lista.length === 0 && <Vazio icon={Users} titulo="Nenhum cliente encontrado" msg="Ajuste a busca ou cadastre um cliente novo." acao={<Btn variant="p" icon={Plus} onClick={() => setNovo(true)}>Novo cliente</Btn>} />}
      </div>

      {/* ficha do cliente */}
      <Modal open={!!c} onClose={() => setSel(null)} title={c?.nome || ""} sub={c ? `${c.tipo === "PJ" ? "CNPJ" : "CPF"} ${c.doc} · cliente desde ${fdata(c.desde)}` : ""} w="max-w-3xl"
        footer={c && <>
          <Btn variant="o" icon={MessageCircle}
            onClick={() => abrirWhatsApp(c.tel, `Olá, ${c.nome.split(" ")[0]}! Aqui é da ${db.oficina?.nome_fantasia || db.oficina?.razao_social || "oficina"}.`, toast)}>WhatsApp</Btn>
          <Btn variant="o" icon={Pencil}
            onClick={() => { setForm({ ...c }); setEditando(c.id); }}>Editar dados</Btn>
          <Btn variant="o" icon={Car} onClick={() => setNovoVei(c.id)}>Adicionar veículo</Btn>
          <Btn variant="p" icon={Plus} onClick={() => { setSel(null); novaOS(c.id); }}>Abrir OS</Btn>
        </>}>
        {c && (() => {
          const vs = veiculosDe(c.id), os = osDe(c.id);
          return (
            <div className="grid gap-5">
              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))" }}>
                <div className="bg-el brd p-3" style={{ borderRadius: "var(--r)" }}>
                  <div className="eyebrow mb-1.5">Contato</div>
                  <div className="text-sm flex items-center gap-1.5 mono"><Phone size={13} className="t-mut2" />{c.tel}</div>
                  <div className="text-sm flex items-center gap-1.5 mt-1"><Mail size={13} className="t-mut2" /><span className="truncate">{c.email || "—"}</span></div>
                  <div className="text-sm flex items-start gap-1.5 mt-1"><MapPin size={13} className="t-mut2 mt-0.5" /><span>{c.end}<br />{c.cidade}</span></div>
                </div>
                <div className="bg-el brd p-3" style={{ borderRadius: "var(--r)" }}>
                  <div className="eyebrow mb-1.5">Histórico</div>
                  <div className="dsp mono" style={{ fontSize: 22 }}>{brl(gastoDe(c.id))}</div>
                  <div className="text-xs t-mut">em {os.length} ordem(ns) de serviço</div>
                  <div className="text-xs t-mut mt-1.5">Última visita: {fdata(ultima(c.id))}</div>
                </div>
                <div className="bg-el brd p-3" style={{ borderRadius: "var(--r)" }}>
                  <div className="eyebrow mb-1.5">Observações</div>
                  <div className="text-sm t-mut" style={{ lineHeight: 1.45 }}>{c.obs || "Nada anotado até agora."}</div>
                </div>
              </div>

              <div>
                <div className="eyebrow mb-2">Veículos ({vs.length})</div>
                <div className="grid gap-2">
                  {vs.map((v) => (
                    <div key={v.id} className="flex flex-wrap items-center gap-3 p-3 brd" style={{ borderRadius: "var(--r)" }}>
                      <Placa n={v.placa} size="md" />
                      <div className="flex-1" style={{ minWidth: 150 }}>
                        <div className="text-sm font-semibold">{v.marca} {v.modelo}</div>
                        <div className="text-xs t-mut">{v.ano} · {v.cor} · {v.comb} · motor {v.motor}</div>
                      </div>
                      <div className="text-right">
                        <div className="mono text-sm font-semibold">{num(v.km)} km</div>
                        <div className="text-xs t-mut2 mono">{v.chassi}</div>
                      </div>
                      {/* Carro vendido, ou lançado no cliente errado. Desliga
                          o vínculo sem tocar no veículo nem no histórico.

                          Nasceu como ícone de 13px com a explicação num title.
                          A oficina reportou que "não conseguia desvincular" — e
                          o log mostra que nenhuma chamada chegou ao servidor:
                          ela nunca achou o botão. title não aparece no toque, e
                          ícone sozinho não se lê. Agora tem nome. */}
                      <Btn variant="o" size="xs" icon={Unlink}
                        onClick={() => setDesvinculando(v)}>Desvincular</Btn>
                    </div>
                  ))}
                  {vs.length === 0 && <div className="text-sm t-mut2">Nenhum veículo vinculado ainda.</div>}
                </div>
              </div>

              <div>
                <div className="eyebrow mb-2">Ordens de serviço</div>
                {os.length === 0 ? <div className="text-sm t-mut2">Esse cliente ainda não teve nenhuma OS.</div> : (
                  <table className="tbl brd" style={{ borderRadius: "var(--r)" }}>
                    <thead><tr><th>OS</th><th>Veículo</th><th>Abertura</th><th>Situação</th><th className="num">Total</th></tr></thead>
                    <tbody>
                      {os.map((o) => (
                        <tr key={o.id} style={{ cursor: "pointer" }} onClick={() => { setSel(null); abrirOS(o.id); }}>
                          <td className="mono text-sm font-semibold">{o.id}</td>
                          <td><Placa n={db.veiculos.find((v) => v.id === o.vei)?.placa || ""} /></td>
                          <td className="text-sm t-mut">{fdata(o.abertura)}</td>
                          <td><Pill t={ST[o.status].c.replace("p-", "")}>{ST[o.status].l}</Pill></td>
                          <td className="num text-sm font-semibold">{brl(totalOS(o))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* novo cliente */}
      <Modal open={novo || !!editando} onClose={() => { setNovo(false); setEditando(null); }}
        title={editando ? "Editar cliente" : "Novo cliente"}
        sub={editando ? "Vale para o histórico inteiro: as OS deste cliente passam a mostrar os dados novos."
                      : "Só o nome é obrigatório. O resto dá para completar depois."}
        footer={<>
          <Btn variant="g" onClick={() => { setNovo(false); setEditando(null); }}>Cancelar</Btn>
          <Btn variant="p" icon={Save} onClick={salvarCliente}>{editando ? "Salvar alterações" : "Cadastrar cliente"}</Btn>
        </>}>
        <div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))" }}>
          <Field label="Tipo"><select className="inp" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}><option value="PF">Pessoa física</option><option value="PJ">Pessoa jurídica</option></select></Field>
          <Field label={form.tipo === "PJ" ? "Razão social" : "Nome completo"} className="sm:col-span-2"><input className="inp" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome do cliente" /></Field>
          <Field label={form.tipo === "PJ" ? "CNPJ" : "CPF"}><input className="inp inp-mono" value={form.doc} onChange={(e) => setForm({ ...form, doc: e.target.value })} placeholder={form.tipo === "PJ" ? "00.000.000/0000-00" : "000.000.000-00"} /></Field>
          <Field label="Telefone / WhatsApp"><input className="inp inp-mono" value={form.tel} onChange={(e) => setForm({ ...form, tel: e.target.value })} placeholder="(35) 90000-0000" /></Field>
          <Field label="E-mail"><input className="inp" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="cliente@email.com" /></Field>
          <Field label="Cidade / UF"><input className="inp" value={form.cidade} onChange={(e) => setForm({ ...form, cidade: e.target.value })} placeholder="Pouso Alegre / MG" /></Field>
          <Field label="Endereço" className="col-span-full"><input className="inp" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} placeholder="Rua, número, bairro" /></Field>
          <Field label="Observações" className="col-span-full" hint="Preferência de contato, histórico de retorno, condições de pagamento."><textarea className="inp" rows={2} value={form.obs} onChange={(e) => setForm({ ...form, obs: e.target.value })} /></Field>
        </div>
      </Modal>

      <Modal open={!!desvinculando} onClose={() => setDesvinculando(null)}
        title="Desvincular este veículo?"
        sub={desvinculando ? `${desvinculando.placa} — ${desvinculando.marca} ${desvinculando.modelo}` : ""}
        footer={<>
          <Btn variant="g" onClick={() => setDesvinculando(null)}>Voltar</Btn>
          <Btn variant="x" icon={Unlink} disabled={ocupado} onClick={() => executar(
            () => acoes.desvincularVeiculo(desvinculando.id),
            () => { setDesvinculando(null); toast("Veículo desvinculado", "Ele continua no sistema, sem dono, e pode ser ligado a outro cliente."); },
            "Não deu para desvincular")}>Desvincular</Btn>
        </>}>
        <div className="text-sm">
          <p className="mb-2">
            O veículo <strong>continua existindo</strong>, com a quilometragem, o chassi e todas as
            ordens de serviço que já passaram por ele. Só deixa de pertencer a este cliente.
          </p>
          <p className="t-mut">
            Serve para carro vendido ou lançado no cliente errado. Depois é só abrir o veículo e
            ligar ao dono certo. As OS antigas não mudam: elas guardam o cliente da época, que é o
            que a nota fiscal daquele serviço diz.
          </p>
        </div>
      </Modal>

      {/* novo veículo */}
      <Modal open={!!novoVei} onClose={() => setNovoVei(null)} title="Adicionar veículo" sub={novoVei ? db.clientes.find((x) => x.id === novoVei)?.nome : ""}
        footer={<><Btn variant="g" onClick={() => setNovoVei(null)}>Cancelar</Btn><Btn variant="p" icon={Save} onClick={salvarVeiculo}>Vincular veículo</Btn></>}>
        <div className="flex justify-center mb-4"><Placa n={fv.placa || "ABC0D00"} size="lg" /></div>
        <div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))" }}>
          <Field label="Placa"><input className="inp inp-mono" style={{ textTransform: "uppercase" }} value={fv.placa} onChange={(e) => setFv({ ...fv, placa: e.target.value.toUpperCase() })} placeholder="ABC1D23" maxLength={8} /></Field>
          <Field label="Marca"><input className="inp" value={fv.marca} onChange={(e) => setFv({ ...fv, marca: e.target.value })} placeholder="Volkswagen" /></Field>
          <Field label="Modelo"><input className="inp" value={fv.modelo} onChange={(e) => setFv({ ...fv, modelo: e.target.value })} placeholder="Gol 1.6 MSI" /></Field>
          <Field label="Ano"><input className="inp inp-mono" value={fv.ano} onChange={(e) => setFv({ ...fv, ano: e.target.value })} placeholder="2020" /></Field>
          <Field label="Cor"><input className="inp" value={fv.cor} onChange={(e) => setFv({ ...fv, cor: e.target.value })} placeholder="Prata" /></Field>
          <Field label="KM atual"><input className="inp inp-mono" value={fv.km} onChange={(e) => setFv({ ...fv, km: e.target.value })} placeholder="0" /></Field>
          <Field label="Combustível"><select className="inp" value={fv.comb} onChange={(e) => setFv({ ...fv, comb: e.target.value })}>{["Flex", "Gasolina", "Etanol", "Diesel", "GNV", "Elétrico", "Híbrido"].map((x) => <option key={x}>{x}</option>)}</select></Field>
          <Field label="Chassi" className="col-span-full"><input className="inp inp-mono" value={fv.chassi} onChange={(e) => setFv({ ...fv, chassi: e.target.value.toUpperCase() })} placeholder="9BW…" maxLength={17} /></Field>
        </div>
      </Modal>
    </div>
  );
}

export { Clientes };
